import { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, ArrowLeft, CheckCircle, DownloadSimple, Package, Printer, Tag, Warning } from 'shared/icons';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { Id, MONO_CLASS, Mono } from '../ui/Mono';
import { Skeleton } from '../ui/Skeleton';
import { useToast } from '../ui/Toast';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from '../warehouse/zplPrinting';
import { useWarehouseLabels } from '../warehouse/warehouseLabels';

// ─────────────────────────────────────────────────────────────────────────────
// IMPRIMIR O LOTE — em ETAPAS, até o operador dizer que deu certo (dono, 26/09):
//   1. MATERIAIS: quais SKUs (com EAN) e quantas unidades saem nesta pilha;
//   2. IMPRESSORA: escolhe e manda — a identificação do lote sai na frente;
//   3. DEU CERTO? → Finalizar (o lote encerra e os envios ficam impressos) ou
//      Reimprimir: do início, ou a partir da ÚLTIMA QUE SAIU CERTA — bipando o
//      código do envio nela (ninguém conta até a 80ª etiqueta de um lote de 100).
//   O ciclo 2–3 se repete até dar certo.
// Se a impressão começou e a tela fechou (a luz caiu), a lista mostra
// "Recuperar": abre aqui perguntando primeiro o que aconteceu.
// Mandar à impressora OU baixar o .zpl registra QUAIS itens entraram na pilha
// (as `ordens` do ZPL): só eles viram impressos ao finalizar — o que ficou de
// fora (sem etiqueta, etiqueta com erro, etiqueta que chegou depois) volta
// para a fila.
// ─────────────────────────────────────────────────────────────────────────────

const PASSO_RECUPERAR = 'recuperar';
const PASSO_MATERIAIS = 'materiais';
const PASSO_IMPRIMIR = 'imprimir';
const PASSO_CONFERIR = 'conferir';
const PASSO_REIMPRIMIR = 'reimprimir';
const PASSO_BIPAR = 'bipar';
const PASSO_FINALIZADO = 'finalizado';

const TITULOS = {
    [PASSO_RECUPERAR]: 'Retomar a impressão',
    [PASSO_MATERIAIS]: '1. Materiais do lote',
    [PASSO_IMPRIMIR]: '2. Imprimir',
    [PASSO_CONFERIR]: '3. Deu certo?',
    [PASSO_REIMPRIMIR]: 'Reimprimir',
    [PASSO_BIPAR]: 'Última etiqueta que saiu certa',
    [PASSO_FINALIZADO]: 'Lote finalizado',
};

/** Uma escolha grande do fluxo (ícone, título, explicação). */
const Escolha = ({ icone: Icone, titulo, texto, onClick, variante = 'secondary', disabled = false }) => (
    <Button variant={variante} onClick={onClick} disabled={disabled} className="w-full !justify-start px-4 py-3 text-left">
        <Icone size={20} className="mr-3 shrink-0" />
        <span>
            <span className="block font-semibold">{titulo}</span>
            {texto ? <span className="block text-xs font-normal opacity-80">{texto}</span> : null}
        </span>
    </Button>
);

/** A carga falhou (rede, servidor): diz o motivo e deixa tentar de novo, em vez de esqueleto eterno. */
const FalhaAoCarregar = ({ mensagem, onTentar }) => (
    <div
        role="alert"
        className="space-y-2 rounded-lg border-2 border-danger-500 bg-danger-soft p-3 text-sm text-danger-text dark:bg-danger-900/40 dark:text-danger-200"
    >
        <p className="flex items-start gap-2">
            <Warning size={16} className="mt-0.5 shrink-0" />
            {mensagem}
        </p>
        <Button variant="secondary" size="sm" onClick={onTentar}>
            <ArrowClockwise size={16} className="mr-1.5" />
            Tentar de novo
        </Button>
    </div>
);

const LotePainelImpressao = ({ lote, onFechar, onMudou }) => {
    // Textos do seletor de impressora vêm do dicionário do armazém.
    const { label: rotuloImpressora } = useWarehouseLabels();
    const toast = useToast();
    const aberto = Boolean(lote);
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(aberto);

    const [passo, setPasso] = useState(PASSO_MATERIAIS);
    const [materiais, setMateriais] = useState(null);
    const [aPartirDe, setAPartirDe] = useState(1);
    const [zpl, setZpl] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [codigoBipado, setCodigoBipado] = useState('');
    const [erroBipe, setErroBipe] = useState(null);
    const [final, setFinal] = useState(null);
    // A cada bipe recusado o campo volta a ter o foco (o `key` remonta o Input).
    const [tentativa, setTentativa] = useState(0);
    // Falha ao carregar materiais / ZPL: mostra o motivo e "Tentar de novo".
    const [erroMateriais, setErroMateriais] = useState(null);
    const [erroZpl, setErroZpl] = useState(null);
    const [tentativaZpl, setTentativaZpl] = useState(0);

    const loteId = lote?.id ?? null;

    // Abrir: pela lista "Imprimir" começa nos materiais; por "Recuperar",
    // pergunta primeiro o que aconteceu com a impressão que ficou no meio.
    useEffect(() => {
        if (!aberto) {
            return;
        }
        setPasso(lote?.modo === PASSO_RECUPERAR ? PASSO_RECUPERAR : PASSO_MATERIAIS);
        setAPartirDe(1);
        setMateriais(null);
        setZpl(null);
        setCodigoBipado('');
        setErroBipe(null);
        setFinal(null);
        setErroMateriais(null);
        setErroZpl(null);
    }, [aberto, lote]);

    const carregarMateriais = useCallback(async () => {
        if (!loteId) {
            return;
        }
        setCarregando(true);
        try {
            setMateriais(await api.getColetaLoteMateriais(loteId));
        } catch (erro) {
            setErroMateriais(erro?.message || 'Não foi possível carregar os materiais do lote.');
        } finally {
            setCarregando(false);
        }
    }, [loteId]);

    useEffect(() => {
        if (aberto && passo === PASSO_MATERIAIS && !materiais && !erroMateriais) {
            carregarMateriais();
        }
    }, [aberto, passo, materiais, erroMateriais, carregarMateriais]);

    // O ZPL do passo de impressão: do início sai com a identificação do lote;
    // a partir de uma etiqueta, só as etiquetas que faltam. `tentativaZpl`
    // refaz a carga depois de uma falha ("Tentar de novo").
    useEffect(() => {
        if (!aberto || passo !== PASSO_IMPRIMIR || !loteId) {
            return undefined;
        }
        let vivo = true;
        setCarregando(true);
        setZpl(null);
        setErroZpl(null);
        api.getColetaLoteZpl(loteId, {
            a_partir_de: aPartirDe,
            incluir_identificacao: aPartirDe === 1 ? 'true' : 'false',
        })
            .then((dados) => {
                if (vivo) {
                    setZpl(dados);
                }
            })
            .catch((erro) => {
                if (vivo) {
                    setErroZpl(erro?.message || 'Não foi possível montar o ZPL do lote.');
                }
            })
            .finally(() => {
                if (vivo) {
                    setCarregando(false);
                }
            });
        return () => {
            vivo = false;
        };
    }, [aberto, passo, loteId, aPartirDe, tentativaZpl]);

    // A pilha SAIU (impressora ou arquivo): registra quais itens entraram nela
    // — só eles viram impressos ao finalizar — e vai para o "deu certo?".
    const registrarPilha = async (mensagem) => {
        await api.postColetaLoteImpressao(loteId, { aPartirDe, ordens: zpl?.ordens ?? [] });
        toast.success(mensagem);
        onMudou?.();
        setPasso(PASSO_CONFERIR);
    };

    const imprimir = async () => {
        if (!zpl?.zpl) {
            return;
        }
        setEnviando(true);
        try {
            const nome = await SendZplToPrinter({ target: selectedPrinter, printers, zpl: zpl.zpl, label: rotuloImpressora });
            // A impressão COMEÇOU: a lista passa a oferecer "Recuperar" se a
            // tela fechar antes de finalizar.
            await registrarPilha(`Lote enviado${nome ? ` — ${nome}` : ''}`);
        } catch (erro) {
            toast.error(erro?.message || 'Falha ao enviar para a impressora.');
        } finally {
            setEnviando(false);
        }
    };

    // Sem impressora à mão: o .zpl baixado é a pilha. Registra igual à
    // impressão — sem isso o lote baixado nunca chegava ao "finalizar".
    const baixar = async () => {
        if (!zpl?.zpl) {
            return;
        }
        setEnviando(true);
        try {
            DownloadZplFile(zpl.zpl, `lote-${lote?.codigo}${aPartirDe > 1 ? `-da-${aPartirDe}` : ''}.zpl`);
            await registrarPilha('Arquivo .zpl baixado — imprima-o e confira a pilha.');
        } catch (erro) {
            toast.error(erro?.message || 'Não foi possível registrar a impressão do lote.');
        } finally {
            setEnviando(false);
        }
    };

    const finalizar = async () => {
        setEnviando(true);
        try {
            const resultado = await api.finalizarColetaLote(loteId);
            setFinal(resultado);
            setPasso(PASSO_FINALIZADO);
            onMudou?.();
        } catch (erro) {
            toast.error(erro?.message || 'Não foi possível finalizar o lote.');
        } finally {
            setEnviando(false);
        }
    };

    // Recomeçar do início: tudo volta a PENDENTE (o que já saiu continua
    // impresso no envio — não volta para a fila).
    const recomecar = async (proximoPasso) => {
        setEnviando(true);
        try {
            await api.postColetaLoteImpressoAte(loteId, 0);
            setAPartirDe(1);
            setMateriais(null);
            onMudou?.();
            setPasso(proximoPasso);
        } catch (erro) {
            toast.error(erro?.message || 'Não foi possível recomeçar o lote.');
        } finally {
            setEnviando(false);
        }
    };

    // O bipe da ÚLTIMA QUE SAIU CERTA: o que veio antes fica impresso e a
    // reimpressão começa na seguinte.
    const confirmarBipe = async () => {
        const codigo = codigoBipado.trim();
        if (!codigo) {
            return;
        }
        setEnviando(true);
        setErroBipe(null);
        try {
            const resultado = await api.postColetaLoteUltimaCorreta(loteId, codigo);
            onMudou?.();
            setCodigoBipado('');
            if (Number(resultado.restantes) === 0) {
                // Não sobra etiqueta para reimprimir (o que falta, se falta, está
                // sem etiqueta): direto para o finalizar.
                const semEtiquetaDepois = Number(resultado.restantes_sem_etiqueta) || 0;
                toast.success(
                    semEtiquetaDepois > 0
                        ? `Essa era a última etiqueta do lote. ${semEtiquetaDepois} envio(s) sem etiqueta ficam de fora e voltam para a fila ao finalizar.`
                        : 'Essa era a última do lote: saiu tudo.',
                );
                setPasso(PASSO_CONFERIR);
                return;
            }
            toast.success(
                `Até a ${resultado.ordem}ª ficou impressa. Reimprimindo da ${resultado.proxima_ordem}ª (faltam ${resultado.restantes}).`,
            );
            setAPartirDe(Number(resultado.proxima_ordem));
            setPasso(PASSO_IMPRIMIR);
        } catch (erro) {
            setErroBipe({ codigo, mensagem: erro?.message || 'Bipe não reconhecido.' });
            setCodigoBipado('');
            setTentativa((atual) => atual + 1);
        } finally {
            setEnviando(false);
        }
    };

    const semEtiqueta = zpl?.sem_etiqueta ?? [];
    const comErro = zpl?.com_erro ?? [];
    // Nada a imprimir (o que falta está sem etiqueta ou com erro): em vez de
    // um "Imprimir" desabilitado sem saída, oferece finalizar.
    const nadaAImprimir = Boolean(zpl) && Number(zpl.etiquetas) === 0;

    return (
        <Modal
            isOpen={aberto}
            onClose={enviando ? undefined : onFechar}
            title={`${TITULOS[passo]} — lote ${lote?.codigo ?? ''}`}
            size={passo === PASSO_MATERIAIS ? '2xl' : 'md'}
        >
            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-mono-200 bg-mono-50 px-4 py-2 dark:border-mono-800 dark:bg-mono-950">
                    <Id value={lote?.codigo} />
                    <span className="text-sm text-mono-600 dark:text-mono-400">{lote?.rotulo ?? ''}</span>
                </div>

                {passo === PASSO_RECUPERAR ? (
                    <div className="space-y-3">
                        <p className="text-sm text-mono-700 dark:text-mono-300">
                            A impressão deste lote começou e não foi finalizada (a luz caiu, a tela fechou…). O que
                            aconteceu com as etiquetas?
                        </p>
                        <Escolha
                            icone={CheckCircle}
                            variante="success"
                            titulo="Saiu tudo certo — finalizar"
                            texto="O lote encerra e os envios que foram para a impressora ficam como impressos."
                            onClick={finalizar}
                            disabled={enviando}
                        />
                        <Escolha
                            icone={Tag}
                            titulo="Reimprimir a partir de uma etiqueta"
                            texto="Bipe o envio da última etiqueta que saiu certa; a reimpressão começa na seguinte."
                            onClick={() => setPasso(PASSO_BIPAR)}
                            disabled={enviando}
                        />
                        <Escolha
                            icone={ArrowClockwise}
                            titulo="Recomeçar do início"
                            texto="Volta para a lista de materiais e imprime o lote inteiro de novo."
                            onClick={() => recomecar(PASSO_MATERIAIS)}
                            disabled={enviando}
                        />
                    </div>
                ) : null}

                {passo === PASSO_MATERIAIS ? (
                    erroMateriais ? (
                        // Limpar o erro basta: o efeito dos materiais carrega de novo.
                        <FalhaAoCarregar mensagem={erroMateriais} onTentar={() => setErroMateriais(null)} />
                    ) : carregando || !materiais ? (
                        <Skeleton className="h-52 w-full" />
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-mono-700 dark:text-mono-300">
                                Confira o que sai nesta pilha: <strong>{materiais.envios}</strong> envio(s).
                            </p>
                            <div className="max-h-80 overflow-y-auto rounded-lg border border-mono-200 dark:border-mono-800">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-mono-50 text-left text-xs uppercase tracking-wide text-mono-500 dark:bg-mono-950">
                                        <tr>
                                            <th className="px-3 py-2">SKU</th>
                                            <th className="px-3 py-2">EAN</th>
                                            <th className="px-3 py-2">Produto</th>
                                            <th className="px-3 py-2 text-right">Unidades</th>
                                            <th className="px-3 py-2 text-right">Envios</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {materiais.materiais.map((linha) => (
                                            <tr key={linha.sku} className="border-t border-mono-100 dark:border-mono-900">
                                                <td className="px-3 py-2">
                                                    <Mono className="font-medium">{linha.sku}</Mono>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Mono>{linha.ean || '—'}</Mono>
                                                </td>
                                                <td className="px-3 py-2 text-mono-700 dark:text-mono-300">
                                                    {linha.titulo || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                                                    {linha.unidades}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">{linha.envios}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {materiais.sem_etiqueta > 0 ? (
                                <p className="flex items-start gap-2 rounded-lg bg-warning-soft p-2 text-sm text-warning-text dark:bg-warning-900/30 dark:text-warning-200">
                                    <Warning size={16} className="mt-0.5 shrink-0" />
                                    {materiais.sem_etiqueta} envio(s) sem etiqueta baixada não saem na pilha.
                                </p>
                            ) : null}
                            {materiais.cancelados > 0 ? (
                                <p className="flex items-start gap-2 rounded-lg bg-danger-soft p-2 text-sm text-danger-text dark:bg-danger-900/30 dark:text-danger-200">
                                    <Warning size={16} className="mt-0.5 shrink-0" />
                                    {materiais.cancelados} envio(s) foram CANCELADOS no Mercado Livre depois que o lote
                                    foi montado — não separe.
                                </p>
                            ) : null}
                            <div className="flex justify-end">
                                <Button variant="primary" onClick={() => setPasso(PASSO_IMPRIMIR)}>
                                    <Printer size={16} className="mr-1.5" />
                                    Próximo: impressora
                                </Button>
                            </div>
                        </div>
                    )
                ) : null}

                {passo === PASSO_IMPRIMIR ? (
                    <div className="space-y-3">
                        {erroZpl ? (
                            <FalhaAoCarregar
                                mensagem={erroZpl}
                                onTentar={() => setTentativaZpl((atual) => atual + 1)}
                            />
                        ) : carregando || !zpl ? (
                            <Skeleton className="h-24 w-full" />
                        ) : nadaAImprimir ? (
                            <p className="text-sm text-mono-700 dark:text-mono-300">
                                Não há etiqueta para imprimir
                                {aPartirDe > 1 ? (
                                    <>
                                        {' '}
                                        da <strong>{aPartirDe}ª</strong> em diante
                                    </>
                                ) : (
                                    <> neste lote</>
                                )}
                                : o que falta está sem etiqueta ou com erro na etiqueta, não sai na pilha e volta para a
                                fila ao finalizar.
                            </p>
                        ) : (
                            <p className="text-sm text-mono-700 dark:text-mono-300">
                                Saem <strong>{zpl.etiquetas}</strong> etiqueta(s) de envio
                                {aPartirDe > 1 ? (
                                    <>
                                        {' '}
                                        — da <strong>{aPartirDe}ª</strong> em diante
                                    </>
                                ) : (
                                    <>, com a identificação do lote na frente</>
                                )}
                                .
                            </p>
                        )}
                        {semEtiqueta.length > 0 ? (
                            <p className="flex items-start gap-2 rounded-lg bg-warning-soft p-2 text-sm text-warning-text dark:bg-warning-900/30 dark:text-warning-200">
                                <Warning size={16} className="mt-0.5 shrink-0" />
                                <span>
                                    {semEtiqueta.length} envio(s) SEM etiqueta baixada não entram na pilha:{' '}
                                    <Mono className="text-xs">
                                        {semEtiqueta.map((linha) => linha.shipment_id).join(', ')}
                                    </Mono>
                                </span>
                            </p>
                        ) : null}
                        {comErro.length > 0 ? (
                            <p className="flex items-start gap-2 rounded-lg bg-warning-soft p-2 text-sm text-warning-text dark:bg-warning-900/30 dark:text-warning-200">
                                <Warning size={16} className="mt-0.5 shrink-0" />
                                <span>
                                    {comErro.length} envio(s) com ERRO na etiqueta não entram na pilha:{' '}
                                    <Mono className="text-xs">
                                        {comErro.map((linha) => linha.shipment_id).join(', ')}
                                    </Mono>
                                </span>
                            </p>
                        ) : null}
                        {nadaAImprimir ? (
                            <div className="flex flex-wrap gap-2">
                                <Button variant="success" onClick={() => setPasso(PASSO_CONFERIR)}>
                                    <CheckCircle size={16} className="mr-1.5" />
                                    Ir para finalizar
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={() => setPasso(aPartirDe === 1 ? PASSO_MATERIAIS : PASSO_REIMPRIMIR)}
                                >
                                    <ArrowLeft size={16} className="mr-1.5" />
                                    Voltar
                                </Button>
                            </div>
                        ) : (
                            <>
                                <ZplPrinterSelect
                                    label={rotuloImpressora}
                                    printers={printers}
                                    localPrinters={localPrinters}
                                    value={selectedPrinter}
                                    onChange={setSelectedPrinter}
                                    disabled={enviando}
                                />
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={imprimir}
                                        loading={enviando}
                                        disabled={!zpl?.zpl}
                                    >
                                        <Printer size={16} className="mr-1.5" />
                                        Imprimir
                                    </Button>
                                    <Button variant="secondary" onClick={baixar} disabled={!zpl?.zpl || enviando}>
                                        <DownloadSimple size={16} className="mr-1.5" />
                                        Baixar .zpl
                                    </Button>
                                    {aPartirDe === 1 ? (
                                        <Button
                                            variant="ghost"
                                            onClick={() => setPasso(PASSO_MATERIAIS)}
                                            disabled={enviando}
                                        >
                                            <ArrowLeft size={16} className="mr-1.5" />
                                            Materiais
                                        </Button>
                                    ) : null}
                                </div>
                            </>
                        )}
                    </div>
                ) : null}

                {passo === PASSO_CONFERIR ? (
                    <div className="space-y-3">
                        <p className="text-sm text-mono-700 dark:text-mono-300">
                            Confira a pilha na impressora. As etiquetas saíram todas certas?
                        </p>
                        <Escolha
                            icone={CheckCircle}
                            variante="success"
                            titulo="Deu certo — finalizar"
                            texto="O lote encerra e os envios que foram para a impressora ficam como impressos (não voltam para a fila)."
                            onClick={finalizar}
                            disabled={enviando}
                        />
                        <Escolha
                            icone={Tag}
                            titulo="Reimprimir"
                            texto="A impressora falhou ou alguma etiqueta saiu errada."
                            onClick={() => setPasso(PASSO_REIMPRIMIR)}
                            disabled={enviando}
                        />
                    </div>
                ) : null}

                {passo === PASSO_REIMPRIMIR ? (
                    <div className="space-y-3">
                        <Escolha
                            icone={Tag}
                            titulo="A partir de uma etiqueta"
                            texto="Bipe o envio da última etiqueta que saiu certa; a reimpressão começa na seguinte."
                            onClick={() => setPasso(PASSO_BIPAR)}
                            disabled={enviando}
                        />
                        <Escolha
                            icone={ArrowClockwise}
                            titulo="Do início"
                            texto="Imprime o lote inteiro de novo, com a identificação na frente."
                            onClick={() => recomecar(PASSO_IMPRIMIR)}
                            disabled={enviando}
                        />
                        <Button variant="ghost" onClick={() => setPasso(PASSO_CONFERIR)} disabled={enviando}>
                            <ArrowLeft size={16} className="mr-1.5" />
                            Voltar
                        </Button>
                    </div>
                ) : null}

                {passo === PASSO_BIPAR ? (
                    <div className="space-y-3">
                        <p className="text-sm text-mono-700 dark:text-mono-300">
                            Bipe o código de barras do <strong>ENVIO</strong> na última etiqueta que saiu CERTA. Ela e as
                            anteriores ficam como impressas; a reimpressão começa na seguinte.
                        </p>
                        <Input
                            key={`bipe-${tentativa}`}
                            autoFocus
                            label="Código do envio (bipe ou digite e tecle Enter)"
                            value={codigoBipado}
                            onChange={(evento) => setCodigoBipado(evento.target.value)}
                            onKeyDown={(evento) => {
                                if (evento.key === 'Enter') {
                                    evento.preventDefault();
                                    confirmarBipe();
                                }
                            }}
                            disabled={enviando}
                            autoComplete="off"
                            className={MONO_CLASS}
                        />
                        {erroBipe ? (
                            <div className="space-y-1 rounded-lg border-2 border-danger-500 bg-danger-soft p-3 text-sm text-danger-text dark:bg-danger-900/40 dark:text-danger-200">
                                <p className="font-semibold">
                                    Bipado: <Mono>{erroBipe.codigo}</Mono>
                                </p>
                                <p>{erroBipe.mensagem}</p>
                            </div>
                        ) : null}
                        <div className="flex flex-wrap gap-2">
                            <Button variant="primary" onClick={confirmarBipe} loading={enviando} disabled={!codigoBipado.trim()}>
                                <Package size={16} className="mr-1.5" />
                                Reimprimir da seguinte
                            </Button>
                            <Button variant="ghost" onClick={() => setPasso(PASSO_REIMPRIMIR)} disabled={enviando}>
                                <ArrowLeft size={16} className="mr-1.5" />
                                Voltar
                            </Button>
                        </div>
                    </div>
                ) : null}

                {passo === PASSO_FINALIZADO ? (
                    <div className="space-y-3">
                        <p className="flex items-center gap-2 text-base font-semibold text-success-text dark:text-success-200">
                            <CheckCircle size={22} className="shrink-0" />
                            Lote {final?.codigo ?? lote?.codigo} finalizado: {final?.impressos ?? 0} envio(s) impresso(s).
                        </p>
                        {Number(final?.sem_etiqueta) > 0 ? (
                            <p className="text-sm text-mono-700 dark:text-mono-300">
                                {final.sem_etiqueta} envio(s) sem etiqueta baixada não saíram e voltaram para a fila.
                            </p>
                        ) : null}
                        {Number(final?.nao_enviados) > 0 ? (
                            <p className="text-sm text-mono-700 dark:text-mono-300">
                                {final.nao_enviados} envio(s) com etiqueta que não entrou em nenhuma pilha impressa
                                (chegou depois da impressão ou deu erro ao montar) não saíram e voltaram para a fila.
                            </p>
                        ) : null}
                        <div className="flex justify-end">
                            <Button variant="primary" onClick={onFechar}>
                                Fechar
                            </Button>
                        </div>
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

export default LotePainelImpressao;
